import { createContext } from 'react';
import { ContextValue, useContextProps } from 'react-aria-components';
import { LabelRequiredText } from './LabelRequiredText';

import {
  Label as AriaLabel,
  LabelProps as AriaLabelProps,
} from 'react-aria-components';

import styles from './Label.module.scss';

export type LabelContextValue = {
  isCompact?: LabelProps['isCompact'];
};

export const LabelContext =
  createContext<ContextValue<LabelContextValue, HTMLLabelElement>>(null);

type LabelProps = AriaLabelProps & {
  /** The content of the label to render */
  children: React.ReactNode;
  /* Whether to render a compact version of the label with smaller typography. */
  isCompact?: boolean;
  /** Greys everything out and disables user interaction. */
  isDisabled?: boolean;
  /** Mark this label as associated with an errored form element.  Changes the label color to red */
  isInvalid?: boolean;
  /** Renders an asterisk on the left and "(required)" on the right. */
  isRequired?: boolean;
};

/**
 * A styled form label. Accepts all standard html `label` attributes.
 *
 * Merges context props from `LabelContext` with local props (preferring local props).
 */
export const Label = (props: LabelProps) => {
  const [contextProps] = useContextProps(props, null, LabelContext);
  const {
    children,
    isCompact,
    isDisabled,
    isInvalid,
    isRequired,
    ...domProps
  } = contextProps;

  return (
    <AriaLabel
      {...domProps}
      className={styles.label}
      data-compact={isCompact}
      data-state={
        [isInvalid && 'invalid', isDisabled && 'disabled']
          .filter(Boolean)
          .join(' ') || undefined
      }
      data-zds
    >
      {children}

      {isRequired && <LabelRequiredText isDisabled={isDisabled} />}
    </AriaLabel>
  );
};
